import React, { useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Text, Tooltip } from "@performyard/py-components";

import { IHeatmapColumn } from "../../../../components/HeatmapTable";

// hooks
import { useCustomFactorsIncludedInSatisfaction } from "../../../../../../api/hooks";
import { useAiSettings } from "../../../../../../api/hooks/defs/ai";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import { ICohortModel } from "../../../../../../api/services/defs/cohort";

// utils
import { getColor } from "../../../../../../utils/getColor";

// config
import Config from "../../../../Config";
// schema
import { ICohortFactorSummaryPoint, ICohortRespondentCountsPoint } from "../../../../schema";

// local schema
interface BaseProps {
	cohortMetaData: { cohorts: ICohortModel[]; [key: string]: unknown };
	factorsData: ICohortFactorSummaryPoint[];
	respondentCounts: ICohortRespondentCountsPoint[];
}

/**
 * This hook is used to generate the sortable cohort data for columns in the left and right tables
 * of the "Cohort Analysis" heatmap within the engagement dashboard's "Cohorts" page.
 */
const useCohortTableColumns = ({
	cohortMetaData,
	factorsData,
	respondentCounts,
}: BaseProps): [IHeatmapColumn[], IHeatmapColumn[]] => {
	// Feature flag checks
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);

	// Company AI settings check
	const { data: aiSettings } = useAiSettings();

	// Custom factors
	const { data: customFactorsData } = useCustomFactorsIncludedInSatisfaction({ enabled: customFactorsEnabled });
	const allFactors = useMemo(() => {
		const customFactorNames = customFactorsEnabled
			? customFactorsData?.customFactors?.map((factor) => factor.name) || []
			: [];
		return [...Config.FACTORS.FACTORS, ...customFactorNames].sort();
	}, [customFactorsEnabled, customFactorsData]);

	// labels for the leftmost column
	const cohortColumnData: { [key: string]: string } = useMemo(() => {
		return {
			all: "All Employees",
			...(cohortMetaData?.cohorts
				.sort((coA, coB) => (coA.name.trim().toLowerCase() > coB.name.trim().toLowerCase() ? 1 : -1))
				.reduce((acc, cur) => {
					acc[cur._id.toString()] = cur.name;
					return acc;
				}, {}) || {}),
		};
	}, [cohortMetaData]);

	// function used to map empty columns for both tables
	const mapEmpty = (col: string) => [
		col,
		{
			columnId: col,
			label: col,
			data: Object.fromEntries(Object.entries(cohortColumnData).map((entry) => [entry[0], null])),
			nullTooltipText: "Metric requires at least three unique respondents",
			...(col === "eNPS" ? { selectBackgroundColor: () => getColor("neutral", 2) } : {}),
		},
	];

	// empty columns for both tables
	const emptyRightTableColumns: { [key: string]: IHeatmapColumn } = Object.fromEntries(
		["Satisfaction", "eNPS"].map(mapEmpty)
	);
	const emptyFactorsTableColumns: { [key: string]: IHeatmapColumn } = Object.fromEntries(allFactors.map(mapEmpty));

	const factorCellRenderer = ({ value, response_count, respondent_count }: { [key: string]: number }) => (
		<div className="size-full">
			<Tooltip
				content={
					<div>
						<Text size="caption" weight="regular">
							Respondents: {respondent_count}
						</Text>
						<Text size="caption" weight="regular">
							Responses: {response_count}
						</Text>
					</div>
				}
				offset={-8}
				asChild
			>
				<div className="grid size-full place-items-center">
					<Text size="paragraph-2" weight="regular">
						{value}
					</Text>
				</div>
			</Tooltip>
		</div>
	);

	const rightTableColumns = useMemo(() => {
		// Pass 1: Collect raw data by cohort
		const eNPSData: {
			[cohort: string]: { score: number; response_count: number; respondent_count: number };
		} = {};
		const satisfactionRatings: { [cohort: string]: number[] } = {};

		if (factorsData) {
			for (const cur of factorsData) {
				if (!cohortColumnData[cur.cohort]) {
					continue;
				}

				if (cur.factor === "eNPS" && typeof cur.eNPS_score === "number") {
					eNPSData[cur.cohort] = {
						score: cur.eNPS_score,
						response_count: cur.response_count,
						respondent_count: cur.respondent_count,
					};
				} else if (cur.average) {
					if (!satisfactionRatings[cur.cohort]) {
						satisfactionRatings[cur.cohort] = [];
					}
					satisfactionRatings[cur.cohort].push(cur.average);
				}
			}
		}

		// Pass 2: Build column objects from collected data
		const rightColumnsMap: { [key: string]: IHeatmapColumn } = { ...emptyRightTableColumns };

		// Build eNPS column
		const eNPSCohorts = Object.keys(eNPSData);
		if (eNPSCohorts.length > 0) {
			rightColumnsMap["eNPS"] = {
				...rightColumnsMap["eNPS"],
				data: {
					...rightColumnsMap["eNPS"].data,
					...Object.fromEntries(
						eNPSCohorts.map((cohort) => [cohort, parseInt(eNPSData[cohort].score.toFixed(0))])
					),
				},
				cellRendererData: Object.fromEntries(
					eNPSCohorts.map((cohort) => [
						cohort,
						{
							value: eNPSData[cohort].score,
							response_count: eNPSData[cohort].response_count,
							respondent_count: eNPSData[cohort].respondent_count,
						},
					])
				),
				cellRenderer: factorCellRenderer,
			};
		}

		// Build Satisfaction column (does not use custom cell renderer)
		const satisfactionCohorts = Object.keys(satisfactionRatings);
		if (satisfactionCohorts.length > 0) {
			rightColumnsMap["Satisfaction"] = {
				...rightColumnsMap["Satisfaction"],
				data: {
					...rightColumnsMap["Satisfaction"].data,
					...Object.fromEntries(
						satisfactionCohorts.map((cohort) => {
							const ratings = satisfactionRatings[cohort];
							const average = ratings.reduce((sum, val) => sum + val, 0) / ratings.length;
							return [cohort, parseFloat(average.toFixed(1))];
						})
					),
				},
			};
		}

		// Add options drawer column to the rightmost position only if feature flags are enabled
		const rightTableColumns = [...Object.values(rightColumnsMap)];

		// Check if there are at least 3 factor scores other than "Overall Satisfaction" and "eNPS"
		const hasEnoughFactorScores =
			factorsData && factorsData.filter((factorData) => factorData.factor !== "eNPS").length >= 3;

		if (aiFeatureFlagEnabled && aiSettings?.engagementCohortInsightsEnabled && hasEnoughFactorScores) {
			// Create options column data that only shows options for cohorts with enough factor scores
			const optionsColumnData: { [key: string]: string } = {};

			Object.keys(cohortColumnData).forEach((cohortId) => {
				// Check if this specific cohort has at least 3 factor scores (excluding eNPS)
				const cohortFactors =
					factorsData?.filter(
						(factorData) => factorData.cohort === cohortId && factorData.factor !== "eNPS"
					) || [];

				// Only show options if cohort has at least 3 factors with scores
				optionsColumnData[cohortId] = cohortFactors.length >= 3 ? "options" : "";
			});

			const optionsColumn: IHeatmapColumn = {
				columnId: "options",
				label: "",
				minWidth: "48px",
				data: optionsColumnData,
				selectBackgroundColor: () => "", // no color for options column
			};
			rightTableColumns.push(optionsColumn);
		}

		return rightTableColumns;
	}, [factorsData, cohortColumnData, aiFeatureFlagEnabled, aiSettings?.engagementCohortInsightsEnabled]);

	const leftTableColumns = useMemo(() => {
		// Pass 1: Collect factor data by factor and cohort
		const factorDataByFactorAndCohort: {
			[factor: string]: {
				[cohort: string]: { average: number; response_count: number; respondent_count: number };
			};
		} = {};

		if (factorsData) {
			for (const cur of factorsData) {
				if (!cohortColumnData[cur.cohort] || !emptyFactorsTableColumns[cur.factor]) {
					continue;
				}
				if (cur?.average !== null) {
					if (!factorDataByFactorAndCohort[cur.factor]) {
						factorDataByFactorAndCohort[cur.factor] = {};
					}
					factorDataByFactorAndCohort[cur.factor][cur.cohort] = {
						average: cur.average,
						response_count: cur.response_count,
						respondent_count: cur.respondent_count,
					};
				}
			}
		}

		// Pass 2: Build column objects from collected data
		const factorsColumnsMap: { [key: string]: IHeatmapColumn } = { ...emptyFactorsTableColumns };

		for (const factor of Object.keys(factorDataByFactorAndCohort)) {
			const cohortData = factorDataByFactorAndCohort[factor];
			const cohorts = Object.keys(cohortData);

			factorsColumnsMap[factor] = {
				...factorsColumnsMap[factor],
				data: {
					...factorsColumnsMap[factor].data,
					...Object.fromEntries(
						cohorts.map((cohort) => [cohort, parseFloat(cohortData[cohort].average.toFixed(1))])
					),
				},
				cellRendererData: Object.fromEntries(
					cohorts.map((cohort) => [
						cohort,
						{
							value: parseFloat(cohortData[cohort].average.toFixed(1)),
							response_count: cohortData[cohort].response_count,
							respondent_count: cohortData[cohort].respondent_count,
						},
					])
				),
				cellRenderer: factorCellRenderer,
			};
		}

		const respondentsColumn = {
			columnId: "respondent_counts",
			label: (
				<Text size="caption" weight="semi-bold">
					{/* no wrapping */}
					<div className="flex h-full items-center whitespace-nowrap">
						Respondents
						<Tooltip
							content={
								<div className="w-[135px]">
									<Text size="caption" weight="regular">
										Reports the number of unique respondents for the selected time period
									</Text>
								</div>
							}
							asChild
						>
							<FontAwesomeIcon
								icon={faCircleInfo}
								size="xs"
								style={{ color: "var(--py-neutral-5)", alignSelf: "flex-start", padding: "1.5px" }}
							/>
						</Tooltip>
					</div>
				</Text>
			),
			data: respondentCounts.reduce((acc, cur: ICohortRespondentCountsPoint) => {
				acc[cur.cohort] = cur.respondent_count;
				return acc;
			}, {}),
			cellRenderer: ({ respondent_count, response_count }: { [key: string]: number }) => (
				<div className="h-full w-full">
					<Tooltip content={`${response_count} Responses`} asChild offset={-8}>
						<div className="grid h-full w-full place-items-center">
							<Text size="paragraph-2" weight="regular">
								{respondent_count}
							</Text>
						</div>
					</Tooltip>
				</div>
			),
			cellRendererData: respondentCounts.reduce((acc, cur: ICohortRespondentCountsPoint) => {
				acc[cur.cohort] = { respondent_count: cur.respondent_count, response_count: cur.response_count };
				return acc;
			}, {}),
			selectBackgroundColor: () => "", // no color for respondent counts column
		};

		// left table columns
		const leftTableColumns = [
			// label column
			{
				columnId: "cohorts",
				label: "Cohort",
				alignLeft: true,
				minWidth: "184px",
				maxTextLength: 176,
				selectBackgroundColor: () => "", // no color for labels
				data: { all: "All Employees", ...cohortColumnData },
			},
			// respodents count column
			respondentsColumn,
			// factors columns
			...Object.values(factorsColumnsMap),
		];

		// return the formatted columns for both tables
		return leftTableColumns;
	}, [cohortColumnData, factorsData, respondentCounts, allFactors]);

	return [leftTableColumns, rightTableColumns];
};

export default useCohortTableColumns;
